/**
 * Parse the issue body that GitHubIssueService renders so the edit
 * form can prefill from existing values.
 *
 * The server appends a single HTML comment with machine-readable
 * metadata that this module looks for:
 *   <!-- arms-meta-v1: {"v":1,"areaCode":"rm-ui","subAreaCode":"sites",
 *                       "siteId":"...","machineSerial":"..."} -->
 *
 * Anything not encoded in the comment (title is on the issue object;
 * category / priority live as labels; description + steps + attachments
 * are visible markdown) is parsed from the human-readable surface.
 *
 * All parsing is best-effort: an issue created directly on GitHub or
 * edited outside the portal produces sensible undefined fallbacks
 * rather than crashing the form.
 */

export type IssueMeta = {
  areaCode?: string;
  subAreaCode?: string;
  siteId?: string;
  machineSerial?: string;
};

export type ExistingAttachment = {
  fileName: string;
  downloadUrl: string;
};

export type ParsedIssueBody = {
  meta: IssueMeta;
  description: string;
  stepsToReproduce: string;
  attachments: ExistingAttachment[];
};

const META_RE   = /<!--\s*arms-meta-v1:\s*(\{[^]*?\})\s*-->/;
const DESC_RE   = /###\s*Description\s*\n([\s\S]*?)(?=\n###\s|\n---|$)/;
const STEPS_RE  = /###\s*Steps to Reproduce\s*\n([\s\S]*?)(?=\n###\s|\n---|$)/;
const ATTACH_RE = /!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/g;

export function parseIssueBody(body: string): ParsedIssueBody {
  return {
    meta:             parseMeta(body),
    description:      cleanSection(body.match(DESC_RE)?.[1] ?? ''),
    stepsToReproduce: cleanSection(body.match(STEPS_RE)?.[1] ?? ''),
    attachments:      parseAttachments(body),
  };
}

function parseMeta(body: string): IssueMeta {
  const m = body.match(META_RE);
  if (!m) return {};
  try {
    const raw = JSON.parse(m[1]) as Record<string, unknown>;
    return {
      areaCode:      typeof raw.areaCode      === 'string' ? raw.areaCode      : undefined,
      subAreaCode:   typeof raw.subAreaCode   === 'string' ? raw.subAreaCode   : undefined,
      siteId:        typeof raw.siteId        === 'string' ? raw.siteId        : undefined,
      machineSerial: typeof raw.machineSerial === 'string' ? raw.machineSerial : undefined,
    };
  } catch {
    return {};
  }
}

function parseAttachments(body: string): ExistingAttachment[] {
  const results: ExistingAttachment[] = [];
  // The "Attachments" section is delimited by the same markdown headers
  // as the rest; restrict the scan to that section so we don't pick up
  // image references in the description.
  const attachIdx = body.indexOf('### Attachments');
  const scanFrom  = attachIdx >= 0 ? body.slice(attachIdx) : body;
  let m: RegExpExecArray | null;
  const re = new RegExp(ATTACH_RE.source, 'g');
  while ((m = re.exec(scanFrom)) !== null) {
    results.push({ fileName: m[1] || 'attachment', downloadUrl: m[2] });
  }
  return results;
}

/**
 * Strip the literal "—" placeholder the server writes when the user
 * left a section blank, so the edit form starts empty instead of
 * pre-filled with the em-dash glyph.
 */
function cleanSection(text: string): string {
  const trimmed = text.trim();
  return trimmed === '—' ? '' : trimmed;
}
